import BackgroundColors from '@root/joinroot.com/src/utils/background-colors';
import CustomFormBlock from '@root/joinroot.com/src/components/blocks/custom-form-block';
import IconTout from '@root/joinroot.com/src/components/sections/icon-tout/icon-tout';
import IconToutSection, { ICON_SIZES } from '@root/joinroot.com/src/components/sections/icon-tout/icon-tout-section';
import LinkButton from '@root/joinroot.com/src/models/link-button';
import PropTypes from '@root/vendor/prop-types';
import RentersZipCodeForm from '@root/joinroot.com/src/components/forms/renters-zip-code-form';
import renderRichText from '@root/joinroot.com/src/utils/render-rich-text';
import { CUSTOM_FORM, RENTERS_ZIP_CODE_FORM } from '@root/joinroot.com/src/constants';
import { constantize } from '@root/core/src/utils/strings';

export default function IconToutSectionBlock({
  content,
  trackClick,
  trackEvent,
}) {
  return (
    <div data-testid={'iconToutSectionBlock'}>
      <IconToutSection
        anchor={content.anchor?.anchorSlug}
        backgroundColor={BackgroundColors.getDefaultedString(content.backgroundColor)}
        body={renderRichText(content.subheadRich)}
        eyebrow={content.eyebrow}
        iconSize={content.iconSize ? content.iconSize.toLowerCase() : ICON_SIZES.LARGE}
        title={content.headline}
        withGrid={false}
      >
        {content.contentfulchildren.map((iconTout) => {
          const FormComponent = buildFormComponent(iconTout.form);
          return (
            <IconTout
              body={renderRichText(iconTout.body)}
              button={buildIconToutButton(iconTout.callToActionButton, trackClick)}
              eyebrow={iconTout.eyebrow}
              form={FormComponent ? (
                <FormComponent
                  analytics={{
                    trackClick,
                    trackEvent,
                  }}
                  content={iconTout.form}
                />
              ) : null}
              icon={iconTout.asset?.file.url}
              iconAltText={iconTout.iconAltText}
              id={iconTout.id}
              key={iconTout.id}
              title={iconTout.headline}
            />
          );
        })}
      </IconToutSection>
    </div>
  );
}

IconToutSectionBlock.propTypes = {
  content: PropTypes.shape({
    anchor: PropTypes.shape({
      id: PropTypes.string,
      anchorSlug: PropTypes.string,
    }),
    backgroundColor: PropTypes.string,
    subheadRich: PropTypes.shape({
      json: PropTypes.object.isRequired,
    }),
    eyebrow: PropTypes.string,
    iconSize: PropTypes.string,
    headline: PropTypes.string,
    contentfulchildren: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        eyebrow: PropTypes.string,
        headline: PropTypes.string,
        body: PropTypes.object,
        asset: PropTypes.shape({
          file: PropTypes.shape({
            url: PropTypes.string.isRequired,
          }).isRequired,
        }),
        iconAltText: PropTypes.string,
        form: PropTypes.shape({
          sys: PropTypes.shape({
            contentType: PropTypes.shape({
              sys: PropTypes.shape({
                id: PropTypes.string.isRequired,
              }).isRequired,
            }).isRequired,
          }).isRequired,
          id: PropTypes.string.isRequired,
        }),
        callToActionButton: PropTypes.shape({
          id: PropTypes.string.isRequired,
          buttonType: PropTypes.string.isRequired,
          buttonText: PropTypes.string.isRequired,
          buttonLink: PropTypes.string.isRequired,
        }),
      }).isRequired
    ).isRequired,
  }).isRequired,
  trackClick: PropTypes.func.isRequired,
  trackEvent: PropTypes.func,
};

function buildIconToutButton(button, trackClick) {
  if (!button) { return; }

  const {
    buttonText, buttonLink, id, buttonType,
  } = button;

  const trackButtonClick = () => trackClick(`CTA_BUTTON_${id}`, {
    buttonText,
    buttonLink,
  });

  return {
    id,
    type: LinkButton.types[constantize(buttonType)],
    onClick: trackButtonClick,
    text: buttonText,
    link: buttonLink,
  };
}

function buildFormComponent(form) {
  const typeOfForm = form?.sys?.contentType?.sys?.id;
  const formTypes = {
    [RENTERS_ZIP_CODE_FORM]: RentersZipCodeForm,
    [CUSTOM_FORM]: CustomFormBlock,
  };

  return formTypes[typeOfForm] || null;
}
